import { useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_SCREENS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useAllDrafts, useMobileScreens } from '@mobileHooks'
import { MobileScreen } from '@mobileTypes'
import { Box, Button, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileScreens = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllScreens = matchPath(ROUTES.CONFIG_MOBILE_SCREENS, pathname)

  const { screens, loading, copyScreen, deleteScreen } = useMobileScreens()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileScreen>('mobile-screen-draft-')

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [screenToCopy, setScreenToCopy] = useState<{ id: number; name: string; internalId: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [screenToDelete, setScreenToDelete] = useState<{ id: number; name: string } | null>(null)

  const handleCreate = () => navigate(ROUTES.CONFIG_MOBILE_SCREENS_CREATE)

  const handleEdit = (id: number) => navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${id}`)

  const handleCopyClick = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) {
      setScreenToCopy({ id: screen.id, name: screen.name, internalId: screen.internalId })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = async (newName: string, newInternalId: string) => {
    if (screenToCopy) {
      const newScreen = await copyScreen(screenToCopy.id, newName, newInternalId)
      if (newScreen) {
        setCopyDialogOpen(false)
        setScreenToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${newScreen.id}`)
      }
    }
  }

  const handleDeleteClick = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) {
      setScreenToDelete({ id: screen.id, name: screen.name })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = async () => {
    if (screenToDelete) {
      await deleteScreen(screenToDelete.id)
      setDeleteDialogOpen(false)
      setScreenToDelete(null)
    }
  }

  const handleExport = (id: number) => {
    const screen = screens.find(s => s.id === id)
    if (screen) exportToJSON(screen, `${screen.name}_config.json`)
  }

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e]),
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = screens
      .filter(s => draftEntityIdMap.has(String(s.id)))
      .map(s => {
        const entry = draftEntityIdMap.get(String(s.id))!
        return { ...s, ...entry.draft.data, id: s.id, _isDraft: true, _isNewDraft: false, _draftKey: entry.key }
      })

    const serverRows = screens.filter(s => !draftEntityIdMap.has(String(s.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [screens, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_SCREENS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_SCREENS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_SCREENS_EDIT}/${params.row.id}`, { state: { restoreDraft: true } })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.id)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.id)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile Screen'
        initialName={screenToCopy?.name || ''}
        existingNames={screens.map(s => s.name)}
        initialInternalId={screenToCopy?.internalId}
        existingInternalIds={screens.map(s => s.internalId)}
        onClose={() => { setCopyDialogOpen(false); setScreenToCopy(null) }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile Screen'
        message='Are you sure you want to delete screen'
        itemName={screenToDelete?.name}
        onClose={() => { setDeleteDialogOpen(false); setScreenToDelete(null) }}
        onConfirm={handleDelete}
      />

      <Box sx={{ ...(!isAllScreens && { display: 'none' }), flex: 1 }}>
        <PageContentLayout sx={{ p: 1 }}>
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                Mobile Screens
              </Typography>
            }
          />

          <Box sx={{ mb: 2, gap: 2, alignItems: 'center' }}>
            <Button variant='contained' onClick={handleCreate}>
              Create Mobile Screen
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid rows={rows} columns={columns} loading={loading} hideFooter disableRowSelectionOnClick />
          </Box>
        </PageContentLayout>
      </Box>
    </>
  )
}
